import { apiFetch } from "../../compare/api/helpers";
import { HttpMethod } from "../../compare/enums/HttpMethod";
import { PageType } from "../enums/PageType";
import {
    IApiAppPolicyOwner,
    IApiApplicationDetails,
    IApiClient,
    IApiComplete,
    IApiContact,
    IApiDisclosure,
    IApiResponseQuestion,
    IApiSignInfo
} from "../interfaces/IApiAppFormData";
import {
    IAFDeclaration,
    IAFQuestionData,
    IAddressInfo,
    IAnswerDataResponse,
    IApplicantList,
    IApplications,
    IApplicationProgress,
    IApplicationSessionContext,
    IComment,
    IContactInfo,
    IDisclosure,
    IDisclosureProps,
    IFeedbackInfo,
    IPaymentDetails,
    IPaymentInfo,
    IPolicyOwnerInfo,
    IShare,
    IPageInfo,
    IAnswerInfo,
    IApplicantInfo,
    IAppFormQuestion,
    IEligiblityOutcome,
    IAnswerProps,
    IDataLayerInfo,
    ISidebarInfo
} from "../interfaces/IAppFormData";
import {
    parseApplicationDetails,
    augmentApplicantData,
    parseContactInfo,
    parseDisclosure,
    parseQuestions,
    parseUserList
} from "./mappers/AppFormMapper";


export const startApplication = async (
    groupId: string,
    providerId: number,
    sessionUserId: string,
    applicants: IApplicantInfo[]): Promise<IApplicationProgress> => {
    var params = {
        groupId: groupId,
        providerId: providerId,
        sessionUserId: sessionUserId,
        applicants: applicants,
    };
    return apiFetch<IApplicationProgress>(`/api/application/start`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(params)
    }).then((res) => augmentApplicantData(res));
};


export const resumeApplication = async (params: IApplicationSessionContext): Promise<IApplicationProgress> => {
    return apiFetch<IApplicationProgress>(`/api/application/resume?clientId=${params.clientId}&uid=${params.uid}&collectionId=${params.collectionId}`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        }
    }).then((res) => augmentApplicantData(res));
};


export const getApplicationData = async (applicationId: number, groupId: string): Promise<IApplicationProgress> => {
    return apiFetch<IApplicationProgress>(`/api/application/${applicationId}/${groupId}/appdata`, {
        method: HttpMethod.Get,
        headers: {
            "content-type": "application/json"
        }
    }).then((res) => augmentApplicantData(res));
};


export const getEligibilityQuestions = async (
    applicationId: number,
    quoteGroupId: string,
    clientNumber: number
): Promise<IAppFormQuestion[]> => {
    return apiFetch<IAppFormQuestion[]>(`/api/application/${applicationId}/eligibility?quoteGroupId=${quoteGroupId}&clientNumber=${clientNumber}`, {
        method: HttpMethod.Get,
        headers: {
            "content-type": "application/json"
        }
    });
};


export const handleEligibilityAnswers = async (
    applicationId: number,
    quoteGroupId: string,
    clientNumber: number,
    params: IAnswerProps[]
): Promise<IEligiblityOutcome> => {
    return apiFetch<IEligiblityOutcome>(`/api/application/${applicationId}/eligibility?quoteGroupId=${quoteGroupId}&clientNumber=${clientNumber}`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(params)
    });
};


export const startMainQuestions = async (params): Promise<IApplications> => {
    return apiFetch<IApiApplicationDetails>(`/api/smart-health/application/create`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(params)
    }).then((res) => parseApplicationDetails(res));
};


export const getDisclosures = async (
    params: IDisclosureProps
): Promise<IDisclosure[]> => {
    const regex = /[;/?:@&=+$,]/g;
    const encodedSearch = encodeURIComponent(params.search.replace(regex, " ").trim());
    return apiFetch<IApiDisclosure>(
        `/api/smart-health/${params.applicantToken}/${params.questionParameter}/${encodedSearch}`,
        {
            method: HttpMethod.Get
        }
    ).then((res) => parseDisclosure(res));
};

export const getCurrentQuestion = async (
    applicantToken: string
): Promise<IAFQuestionData> => {
    return apiFetch<IApiResponseQuestion>(
        `/api/smart-health/${applicantToken}/question`,
        {
            method: HttpMethod.Get
        }
    ).then((res) => parseQuestions(res));
};

export const submitAnswer = async (
    applicantToken: string,
    params: IAnswerDataResponse
): Promise<IAFQuestionData> => {
    return apiFetch<any>(`/api/smart-health/${applicantToken}/submit`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(params)
    }).then((res) => parseQuestions(res));
};

export const getPreviousQuestion = async (
    applicantToken: string
): Promise<IAFQuestionData> => {
    return apiFetch<IApiResponseQuestion>(`/api/smart-health/${applicantToken}/back`, {
        method: HttpMethod.Get
    }).then((res) => parseQuestions(res));
};

export const skipCurrentQuestion = async (
    applicantToken: string
): Promise<IAFQuestionData> => {
    return apiFetch<IApiResponseQuestion>(`/api/smart-health/${applicantToken}/skip`, {
        method: HttpMethod.Get
    }).then((res) => parseQuestions(res));
};

export const getDeclaration = async (applicantToken: string): Promise<IAFDeclaration> => {
    return apiFetch<IAFDeclaration>(
        `/api/smart-health/${applicantToken}/declaration`,
        {
            method: HttpMethod.Get
        }
    );
};

export const recordAcceptance = async (applicantToken: string): Promise<IApiComplete> => {
    return apiFetch<IApiComplete>(`/api/smart-health/${applicantToken}/accept`, {
        method: HttpMethod.Post
    });
};

// Setup Section
export const getUserList = async (groupId): Promise<IApplicantList> => {
    return apiFetch<IApiClient>(`/api/v1/groups/${groupId}`, {
        method: HttpMethod.Get
    }).then((res) => parseUserList(res?.data));
};

export const getSectionContent = async (
    applicationId: number,
    quoteGroupId: string
): Promise<any> => {
    // This returns a dictionary, which we should just keep as a generic object
    return apiFetch<any>(
        `/api/application/${applicationId}/section-content?quoteGroupId=${quoteGroupId}`,
        {
            method: HttpMethod.Get
        }
    );
};

// Application Final Section
export const getApplicationPolicyOwners = async (
    applicationId,
    quoteGroupId: string
): Promise<IApiAppPolicyOwner[]> => {
    return apiFetch<IApiAppPolicyOwner[]>(
        `/api/application/${applicationId}/${quoteGroupId}/policy-owners`,
        {
            method: HttpMethod.Get
        }
    );
};

export const removePolicyOwner = async (
    applicationId: number,
    quoteGroupId: string,
    appPolicyOwnerId: number
): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/application/${applicationId}/${quoteGroupId}/policy-owner/${appPolicyOwnerId}`,
        {
            method: HttpMethod.Delete
        }
    );
};

export const addPolicyOwner = async (
    applicationId: number,
    quoteGroupId: string,
    info: IPolicyOwnerInfo
): Promise<void> => {
    return apiFetch<void>(
        `/api/application/${applicationId}/${quoteGroupId}/policy-owner`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify({ ...info })
        }
    );
};

export const addExistingPolicyOwner = async (
    applicationId: number,
    quoteGroupId: string,
    applicantId: number
): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/application/${applicationId}/${quoteGroupId}/applicant/${applicantId}/policy-owner`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            }
        }
    );
};

export const getPaymentDetails = async (
    applicationId: number,
    quoteGroupId: string
): Promise<IPaymentDetails> => {
    return apiFetch<IPaymentDetails>(
        `/api/application/${applicationId}/${quoteGroupId}/payment-details`,
        {
            method: HttpMethod.Get
        }
    );
};

export const addPayment = async (
    applicationId: number,
    quoteGroupId: string,
    info: IPaymentInfo
): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/application/${applicationId}/${quoteGroupId}/payment-details`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(info)
        }
    );
};

export const getAddress = async (
    applicationId: number,
    quoteGroupId: string,
    applicantId: number
): Promise<Array<IAddressInfo>> => {
    return apiFetch<Array<IAddressInfo>>(
        `/api/application/${applicationId}/${quoteGroupId}/applicant/${applicantId}/address-details`,
        {
            method: HttpMethod.Get
        }
    );
};

export const addAddress = async (
    applicationId: number,
    quoteGroupId: string,
    info: IAddressInfo
): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/application/${applicationId}/${quoteGroupId}/address-details`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(info)
        }
    );
};

export const sign = async (
    applicationId: number,
    quoteGroupId: string,
    applicantId: number,
    sign: IApiSignInfo
): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/application/${applicationId}/${quoteGroupId}/applicant/${applicantId}/sign`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(sign)
        }
    );
};

export const getFeedback = async (
    applicationId: number,
    quoteGroupId: string
): Promise<IFeedbackInfo> => {
    return apiFetch<IFeedbackInfo>(
        `/api/application/${applicationId}/${quoteGroupId}/feedback`,
        {
            method: HttpMethod.Get
        }
    );
};

export const sendFeedback = async (
    applicationId: number,
    quoteGroupId: string,
    feedback: IFeedbackInfo
): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/application/${applicationId}/${quoteGroupId}/feedback`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(feedback)
        }
    );
};

export const agreeDisclosure = async (
    applicationId: number,
    quoteGroupId: string
): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/application/${applicationId}/${quoteGroupId}/disclosure`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            }
        }
    );
};

export const cleanSkin = async (
    applicationId: number,
    quoteGroupId: string,
    cleanSkin: boolean
): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/application/${applicationId}/${quoteGroupId}/cleanskin`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(cleanSkin)
        }
    );
};

export const setCurrentPageProgress = async (
    applicationId: number,
    quoteGroupId: string,
    page: IPageInfo
): Promise<void> => {
    return apiFetch<void>(`/api/application/${applicationId}/${quoteGroupId}/page`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(page)
    });
};


export const getContactInfo = async (quoteGroupId: string): Promise<IContactInfo> => {
    return apiFetch<IApiContact>(`/api/v1/groups/${quoteGroupId}/contact`, {
        method: HttpMethod.Get
    }).then((res) => parseContactInfo(res?.data));
};

interface IApiRes {
    d: string;
}
export const emailCB = async (data: IShare): Promise<IApiRes> => {
    return apiFetch(`/lightwindow/tellafriend.aspx/EmailCB`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(data)
    });
};

export const sendFinalComments = async (applicationId: number, quoteGroupId: string, comment: string): Promise<IApiRes> => {
    return apiFetch(`api/application/${applicationId}/${quoteGroupId}/comments`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(comment)
    });
};


export const getReferTerms = async (): Promise<string> => {
    return await fetch(`/lightwindow/tellafriend-terms.aspx`, {
        method: HttpMethod.Get,
        headers: {
            "Content-Type": "text/plain; charset=utf-8"
        }
    }).then((res) => res.text());
};

export const getReferBonusTerms = async (): Promise<string> => {
    return await fetch(`/lightwindow/referfriend.aspx`, {
        method: HttpMethod.Get,
        headers: {
            "Content-Type": "text/plain; charset=utf-8"
        }
    }).then((res) => res.text());
};

export const getResponses = async (
    quoteGroupId: string,
    applicationId: number,
    clientNumber: number,
    currentPage: PageType): Promise<any> => {
    return apiFetch<any>(`/api/application/${applicationId}/${quoteGroupId}/applicant${clientNumber}/questions/${currentPage}`, {
        method: HttpMethod.Get,
        headers: {
            "content-type": "application/json"
        }
    });
}

export const saveResponses = async (
    quoteGroupId: string,
    applicationId: number,
    clientNumber: number,
    fieldResponses: object): Promise<any> => {
    return apiFetch<any>(`/api/application/${applicationId}/${quoteGroupId}/applicant${clientNumber}/responses`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(fieldResponses)
    });
}


export const saveAnswer = async (
    quoteGroupId: string,
    applicationId: number,
    answerInfo: IAnswerInfo): Promise<any> => {
    return apiFetch<any>(`/api/application/${applicationId}/${quoteGroupId}/answer`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(answerInfo)
    });
}

export const getDataLayerInfo = async (
    applicationId: number,
    quoteGroupId: string
): Promise<IDataLayerInfo> => {
    return apiFetch<IDataLayerInfo>(
        `/api/application/${applicationId}/${quoteGroupId}/datalayer`,
        {
            method: HttpMethod.Get
        }
    );
};

export const getSidebarInfo = async (
    applicationId: number,
    quoteGroupId: string
): Promise<ISidebarInfo> => {
    return apiFetch<ISidebarInfo>(
        `/api/application/${applicationId}/${quoteGroupId}/sidebar`,
        {
            method: HttpMethod.Get
        }
    );
};